import { useFieldArray, useForm } from "react-hook-form";
import { CategoryModal } from "../CategoryGrid";
import * as React from "react";
import { FormSelect, NumericFormInput } from "../../../../../../components/Forms";
import { useTranslation } from "react-i18next";
import { nonNumericSymbols, VALIDATION_PATTERNS } from "../../../../../../utils";
import { flattenDimensionItems, renderDimensionFormCells } from "../Category/helpers";
import { useDimensionFiltering } from "../Category/useDimensionFiltering";
import {
  getLabel,
  renderSubTypeOfTransportOptions,
  renderSubTypeTwoOptions,
  renderTypeOfTransportOptions,
  renderUnitOptions,
  transportTypesWithRequiredSubtypeTwo
} from "../Category/transportationAndDistributionHelpers";

function TransportationAndDistributionModalWrapper({
                                                     tabKey,
                                                     modalData,
                                                     attributesOrder,
                                                     isOpen,
                                                     onClose,
                                                     onSubmit,
                                                     getEmptyRow,
                                                     transportTypes
                                                   }) {
  const { fieldArrayName, editData, organizationStructure, dimensionItems } = modalData;
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const labelKey = "transportationAndDistribution";

  const seededEditData = editData ? flattenDimensionItems(editData) : null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    resetField
  } = useForm({
    defaultValues: {
      [fieldArrayName]: [seededEditData ? seededEditData : getEmptyRow()]
    }
  });

  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: fieldArrayName
  });

  const watchFieldArray = watch(fieldArrayName);

  const { getFilteredItems, setChangedRowIndex } = useDimensionFiltering({
    dimensionItems,
    editData: seededEditData,
    watchFieldArray
  });

  const mapRow = (row, index) => {
    const gridItems = [];

    // ESG-3894: prepend dimension dropdowns
    gridItems.push(...renderDimensionFormCells({
      organizationStructure,
      dimensionItems,
      register,
      fieldArrayName,
      index,
      watchFieldArray,
      getFilteredItems,
      onDimensionChange: setChangedRowIndex
    }));

    const typeOfTransportSelector = register(`${fieldArrayName}.${index}.typeOfTransport`, {
      required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.typeOfTransport.name`) })
    });
    const subtypeOfTransportSelector = register(`${fieldArrayName}.${index}.subtypeOfTransport`, {
      required: globalTranslation("entityRequired", { entityName: t(`inputs.${tabKey}.subtypeOfTransport.name`) })
    });

    gridItems.push(
      <FormSelect
        errors={errors}
        {...typeOfTransportSelector}
        onChange={(e) => {
          typeOfTransportSelector.onChange(e);
          resetField(`${fieldArrayName}.${index}.subtypeOfTransport`, { defaultValue: "" });
          resetField(`${fieldArrayName}.${index}.subtypeTwo`, { defaultValue: "" });
          resetField(`${fieldArrayName}.${index}.unit`, { defaultValue: "" });
        }}
        placeholder=" "
        title={watchFieldArray[index].typeOfTransport !== "" ?
          getLabel(
            "typeOfTransport",
            watchFieldArray[index].typeOfTransport,
            transportTypes?.find(el => el.code === watchFieldArray[index].typeOfTransport)?.name ?? ""
          ) : ""
        }
      >
        {renderTypeOfTransportOptions(transportTypes)}
      </FormSelect>
    );
    gridItems.push(
      <FormSelect
        errors={errors}
        {...subtypeOfTransportSelector}
        onChange={(e) => {
          subtypeOfTransportSelector.onChange(e);
          resetField(`${fieldArrayName}.${index}.subtypeTwo`, { defaultValue: "" });
        }}
        placeholder=" "
        title={watchFieldArray[index].subtypeOfTransport !== "" ?
          getLabel(
            "subtypeOfTransport",
            watchFieldArray[index].subtypeOfTransport,
            transportTypes?.find(type => type.code === watchFieldArray[index].typeOfTransport)?.subtypes?.find
            (subtype => subtype.code === watchFieldArray[index].subtypeOfTransport)?.name ?? ""
          ) : ""
        }
      >
        {renderSubTypeOfTransportOptions(transportTypes, watchFieldArray[index].typeOfTransport)}
      </FormSelect>
    );
    gridItems.push(
      <FormSelect
        errors={errors}
        {...register(`${fieldArrayName}.${index}.subtypeTwo`, {
          required: transportTypesWithRequiredSubtypeTwo.includes(watchFieldArray[index].typeOfTransport) ?
            globalTranslation("entityRequired", { entityName: t(`inputs.${labelKey}.subtypeTwo.name`) }) : false
        })}
        placeholder=" "
        title={watchFieldArray[index].subtypeTwo !== "" ?
          getLabel(
            "subtypeTwo",
            watchFieldArray[index].subtypeTwo,
            transportTypes?.find(type => type.code === watchFieldArray[index].typeOfTransport)?.subtypes?.find
            (subtype => subtype.code === watchFieldArray[index].subtypeOfTransport)?.subtypeTwos?.find
            (subtype2 => subtype2.code === watchFieldArray[index].subtypeTwo)?.name ?? ""
          ) : ""
        }
      >
        {renderSubTypeTwoOptions(transportTypes, watchFieldArray[index].typeOfTransport, watchFieldArray[index].subtypeOfTransport)}
      </FormSelect>
    );
    gridItems.push(
      <FormSelect
        errors={errors}
        {...register(`${fieldArrayName}.${index}.unit`, {
          required: globalTranslation("entityRequired",
            { entityName: t(`inputs.${labelKey}.unit.name`) })
        })}
        placeholder=" "
        title={watchFieldArray[index].unit !== "" ?
          getLabel(
            "unit",
            watchFieldArray[index].unit,
            transportTypes?.find(type => type.code === watchFieldArray[index].typeOfTransport)?.units?.find
            (unit => unit.code === watchFieldArray[index].unit)?.name ?? ""
          ) : ""
        }
      >
        {renderUnitOptions(transportTypes, watchFieldArray[index].typeOfTransport)}
      </FormSelect>
    );
    gridItems.push(
      <NumericFormInput
        errors={errors}
        {...register(`${fieldArrayName}.${index}.value`, {
          required: globalTranslation("entityRequired",
            { entityName: t(`inputs.${labelKey}.value`) }),
          pattern: VALIDATION_PATTERNS.POSITIVE_NUMBER
        })}
        onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
        type="number"
      />
    );

    return gridItems;
  };

  return (
    <CategoryModal
      modalData={modalData}
      formData={{ handleSubmit, fields, append, remove }}
      attributesOrder={attributesOrder}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      getEmptyRow={getEmptyRow}
      mapRow={mapRow}
    />
  );
}

export default TransportationAndDistributionModalWrapper;